// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
/**
 * Canonical field mapping utilities — Sprint 37.1.0
 * Resolves template field IDs to canonical keys and vice versa.
 */
import { CANONICAL_FIELDS } from '../constants/canonical-fields.js';
// Build lookup indexes once at module load
const fieldIdToCanonical = new Map();
for (const def of CANONICAL_FIELDS) {
    for (const fieldId of def.template_field_ids) {
        // First registration wins (matches template_field_ids ordering = preferred source)
        if (!fieldIdToCanonical.has(fieldId)) {
            fieldIdToCanonical.set(fieldId, def);
        }
    }
}
const canonicalKeyToDefinition = new Map();
for (const def of CANONICAL_FIELDS) {
    canonicalKeyToDefinition.set(def.key, def);
}
/**
 * Given a template field_id, find its canonical key.
 * Returns null if the field is template-specific (not in the canonical registry).
 */
export function getCanonicalKey(fieldId) {
    return fieldIdToCanonical.get(fieldId)?.key ?? null;
}
/**
 * Given a canonical key, find the preferred template field_id.
 * If templateFieldIds are provided, returns the first match from the canonical
 * definition's ordered list that exists in the template.
 */
export function getTemplateFieldId(canonicalKey, templateFieldIds) {
    const def = canonicalKeyToDefinition.get(canonicalKey);
    if (!def)
        return null;
    if (!templateFieldIds) {
        return def.template_field_ids[0] ?? null;
    }
    const templateSet = new Set(templateFieldIds);
    for (const fid of def.template_field_ids) {
        if (templateSet.has(fid))
            return fid;
    }
    return null;
}
/**
 * Get the full canonical field definition by key.
 */
export function getCanonicalDefinition(key) {
    return canonicalKeyToDefinition.get(key) ?? null;
}
/**
 * Get all canonical field definitions for a given module.
 */
export function getCanonicalFieldsForModule(moduleType) {
    return CANONICAL_FIELDS.filter((def) => def.modules.includes(moduleType));
}
/**
 * Structure flat field_values into _canonical and _template partitions.
 * The flat map remains authoritative; partitions are derived read-only views.
 */
export function structureFieldValues(flatValues) {
    const canonical = {};
    const template = {};
    for (const [fieldId, value] of Object.entries(flatValues)) {
        // Skip internal partition keys
        if (fieldId === '_canonical' || fieldId === '_template')
            continue;
        const canonicalKey = getCanonicalKey(fieldId);
        if (canonicalKey) {
            // First value wins for each canonical key (preferred source ordering)
            if (!(canonicalKey in canonical)) {
                canonical[canonicalKey] = value;
            }
        }
        else {
            template[fieldId] = value;
        }
    }
    return { _canonical: canonical, _template: template };
}
//# sourceMappingURL=field-mapping.js.map